import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { submitLead, formatApiError } from "@/lib/api";
import { captureUtms, pushEvent } from "@/lib/tracking";

const EMPTY = { name: "", phone: "", email: "", message: "" };

export default function LeadForm({ source = "Website", page = "", dark = false, compact = false }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const honeypotRef = useRef(null);
  const utmsRef = useRef({});

  useEffect(() => {
    utmsRef.current = captureUtms();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // Honeypot: if filled, silently pretend success (bot).
    if (honeypotRef.current && honeypotRef.current.value) {
      setDone(true);
      return;
    }
    if (!form.name || !form.phone || !form.email) {
      toast.error("Udfyld navn, telefon og e-mail.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitLead({ ...form, source, page, utm: utmsRef.current });
      setDone(true);
      setForm(EMPTY);
      pushEvent("lead_submit", { form_source: source, form_page: page, ...utmsRef.current });
      toast.success(res.message || "Tak for din henvendelse.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke sende. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `block text-xs uppercase tracking-label mb-2 ${dark ? "text-background/50" : "text-muted-foreground"}`;
  const inputCls = `w-full bg-transparent border-b py-3 outline-none font-light transition-colors ${
    dark
      ? "border-background/25 text-background placeholder-background/30 focus:border-background"
      : "border-border text-foreground placeholder-muted-foreground/50 focus:border-foreground"
  }`;

  if (done) {
    return (
      <div data-testid="lead-form-success" className={`py-12 ${dark ? "text-background" : "text-foreground"}`}>
        <p className="text-xs uppercase tracking-label text-accent mb-4">Tak</p>
        <h3 className="font-display text-3xl md:text-4xl font-light">Vi har modtaget din henvendelse.</h3>
        <p className={`mt-4 font-light ${dark ? "text-background/70" : "text-muted-foreground"}`}>
          Vi vender tilbage hurtigst muligt for at aftale et uforpligtende inspirationsmøde.
        </p>
        <button
          onClick={() => setDone(false)}
          className={`mt-6 text-sm uppercase tracking-label border-b pb-1 ${dark ? "border-background/40" : "border-foreground/40"}`}
          data-testid="lead-form-reset"
        >
          Send en ny
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-6">
      {/* Honeypot — invisible field for bot detection */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
        <label>Website (lad denne stå tom)
          <input ref={honeypotRef} type="text" name="website" tabIndex="-1" autoComplete="off" />
        </label>
      </div>
      <div className={compact ? "space-y-6" : "grid sm:grid-cols-2 gap-6"}>
        <div>
          <label className={labelCls}>Navn</label>
          <input data-testid="lead-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Dit navn" />
        </div>
        <div>
          <label className={labelCls}>Telefon</label>
          <input data-testid="lead-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Telefonnummer" />
        </div>
      </div>
      <div>
        <label className={labelCls}>E-mail</label>
        <input data-testid="lead-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="din@mail.dk" />
      </div>
      <div>
        <label className={labelCls}>Om dit projekt</label>
        <textarea data-testid="lead-message" rows={compact ? 3 : 4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Fortæl kort om dit hus og dine ønsker (valgfrit)" />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit"
        className={`group inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-label font-semibold transition-colors disabled:opacity-50 ${
          dark ? "bg-background text-foreground hover:bg-background/90" : "bg-foreground text-background hover:bg-accent"
        }`}
      >
        {loading ? "Sender..." : "Send henvendelse"}
        <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
      </button>
      <p className={`text-xs font-light ${dark ? "text-background/50" : "text-muted-foreground"}`}>
        Vi bruger kun dine oplysninger til at behandle din henvendelse. Se vores{" "}
        <a href="/privatlivspolitik" className={`underline underline-offset-2 ${dark ? "hover:text-background" : "hover:text-foreground"}`}>privatlivspolitik</a>.
      </p>
    </form>
  );
}
